<template>
	<div>
		<div v-if="eventInfo.event_image" class="pa-0 event-description__thumbnail-wrapper">
			<div>
				<img
					class="event-description__thumbnail-image"
					:src="eventInfo.event_image"
					alt="event image"
					loading="lazy"
				/>
				<SponsorBadge
					v-if="sponsoredLabel"
					:text="sponsoredLabel"
					:top="'0px'"
					:right="'0px'"
					:icon="'star'"
					:iconSize="10"
					:iconColor="'#ffffff'"
				/>
			</div>
		</div>
		<v-card
			:style="isPadding"
			class="event-description__wrapper elevation-0"
		>
      <SponsorBadge
          v-if="sponsoredLabel && eventInfo.event_image === null"
          :text="sponsoredLabel"
          :top="'0px'"
          :right="'0px'"
          :icon="'star'"
          :iconSize="10"
          :iconColor="'#ffffff'"
      />
			<v-card-text class="event-description__title px-3 pb-0">
				{{ eventInfo.event_title }}
			</v-card-text>

			<v-card-text class="event-description__date px-3 d-flex align-end">
				<Icon :size="16" type="union" :color="'#4a4a4a'" />
				<span class="ml-2">
            {{prettyEventRange}}
				</span>
			</v-card-text>
			<div
				v-if="hasAddress"
				class="event-description__address-wrapper mt-2 px-3"
			>
        <p v-for="(line, index) in addressLines" :key="index" class="event-description__address">
          {{ line }}
        </p>
			</div>
			<div
				class="event-description__tags flex-wrap px-3 mt-3"
				v-if="eventInfo.tags && eventInfo.tags.length"
			>
				<v-chip
					v-for="(tag, index) in tagView"
					:color="tag.tag_bg_color"
					:text-color="tag.tag_txt_color"
					class="post-preview-card__tag mr-2"
					:key="index"
				>
					#{{ tag.tag }}
				</v-chip>
				<div
					v-if="!isAllTag && eventInfo.tags.length > 2"
					@click="tagsToggle"
					class="event-preview-card__more-tags"
				>
					+{{ eventInfo.tags.length - 2 }} more
				</div>
				<div
					v-if="isAllTag && eventInfo.tags.length > 2"
					@click="tagsToggle"
					class="event-preview-card__more-tags"
				>
					hide
				</div>
			</div>

			<v-divider v-if="isButtonVisible" class="event-description__divider" />
			<EventRegisterButton
				v-if="isButtonVisible"
				:isExternal="isExternal"
				:isFollowed="!!isFollowed"
				:registerLink="eventInfo.event_register_link"
				@follow="onFollow"
			/>
		</v-card>
	</div>
</template>

<script>
import { followEvent, unfollowEvent } from '@/api/event'
import { mapActions, mapGetters } from 'vuex'
import pinnedIcon from '@/assets/images/icon-pinned.svg'
import EventNavbar from '@/components/Event/EventNavbar.vue'
import ProfileBadge from '@/components/ProfileBadge.vue'
import Dwh from '@/mixins/Dwh'
import EventRegisterButton from '@/components/Event/EventRegisterButtons.vue'
import SponsorBadge from '@/components/SponsorBadge.vue'
import {
  formatEventRangeLine,
} from '@/utils/timezone'

export default {
	name: 'EventDescription',
	mixins: [Dwh],
	components: {
		SponsorBadge,
		EventRegisterButton,
		EventNavbar,
		ProfileBadge,
	},
	data() {
		return {
			isFollowed: 0,
			pinnedIcon,
			isAllTag: false,
		}
	},
	props: {
		eventInfo: {
			type: Object,
			default: () => ({}),
		},
	},
	computed: {
		...mapGetters('settings', ['eventSettings']),
    isButtonVisible(){
      return this.eventInfo.event_register_link || this.isFollowed
    },
    isPadding(){
      return {
        paddingBottom: this.eventInfo.event_register_link || this.isFollowed  ? '0px' : '12px',
      }
    },
    eventTimezone() {
      return this.eventInfo.event_timezone || this.eventInfo.eventTimezone.timezone_code || ''
    },
    prettyEventRange() {
      const tz = this.eventInfo?.eventTimezone;
      return formatEventRangeLine(
          this.eventInfo.event_start,
          this.eventInfo.event_end,
          tz,
          'en-US'
      )
    },
		sponsoredLabel() {
			const sponsor = this.eventInfo.event_sponsor_text
			if (!sponsor) return ''

			const limitedSponsor = sponsor.length > 30 ? sponsor.slice(0, 30) + '…' : sponsor

			return `Sponsored by ${limitedSponsor}`
		},
		tagView() {
			const eventTags = this.eventInfo.tags
			return this.isAllTag === false ? eventTags.slice(0, 2) : eventTags
		},
		isExternal() {
			return this.eventInfo.event_register_link?.startsWith('http')
		},
    hasAddress() {
      return this.eventInfo.address != null
    },
    addressLines() {
      const a = this.eventInfo.address || {}
      const country = a.address_country === 'United States' ? '' : a.address_country

      const cityLine = [
        a.address_city,
        a.address_state,
        country,
        a.address_zip
      ].filter(Boolean).join(', ')

      return [
        a.address_line1,
        a.address_line2,
        cityLine
      ].filter(Boolean)
    }
	},
	methods: {
		...mapActions('event', [
			'getEventPrivate',
			'addEventToSchedule',
			'removeEventFromSchedule',
		]),
		tagsToggle() {
			this.isAllTag = !this.isAllTag
		},
    handleRegisterEvent() {
			this.sendTrigger({
				category: 'Content',
				action: 'click_register_button_event',
				module: 'Events',
				data: this.eventInfo.event_id,
				title: this.eventInfo.event_title,
				content_chapter_id: this.eventInfo.chapter_id ? [this.eventInfo.chapter_id] : null,
			})
		},
		async onFollow() {
			if (this.isFollowed) {
				await unfollowEvent(this.eventInfo.event_id)
			} else if (!this.isFollowed) {
				await followEvent({ event_id: this.eventInfo.event_id })
				this.handleRegisterEvent()
			}
			if (this.eventSettings.restrict_event_details_by_registration === 1) {
				await this.getEventPrivate(this.$route.params.id)
			}

			this.isFollowed = !this.isFollowed
		},
	},
	created() {
		if (this.eventInfo) {
			this.isFollowed = this.eventInfo.is_follow
		}
	},
}
</script>

<style lang="scss">
.event-description {
	&__tags {
		display: flex;
		flex-wrap: wrap;
		gap: 6px 0;
	}
	&__wrapper {
		background: #fff;
		margin-bottom: 8px;
		border-radius: 8px !important;
		padding-top: 12px !important;
		border: 1px solid #e2e2e2 !important;
	}

	&__title {
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 20px;
		line-height: 22px;
		letter-spacing: 0p;
		color: var(--username-category-title) !important;
		margin-bottom: 12px;
	}

	&__date {
		font-family: Inter, sans-serif;
		font-weight: 400;
		font-size: 13px;
		line-height: 100%;
		letter-spacing: 0;
		color: #4a4a4a !important;
	}

	&__address {
    display: flex;
    flex-direction: column;

		font-family: Inter, sans-serif;
		font-weight: 500;
		font-size: 13px;
		line-height: 100%;
		letter-spacing: 0;
		color: #4a4a4a;
		margin-bottom: 0 !important;
	}

	&__panel {
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
		justify-content: space-between;
		padding-right: 16px;
		border-bottom: 1px solid #e2e2e2;

		@media (max-width: 768px) {
			flex-direction: column-reverse;
			padding: 0;
		}

		&-buttons {
			display: flex;
			flex-wrap: nowrap;
			column-gap: 8px;

			@media (max-width: 768px) {
				width: calc(100% - 32px);
				margin: 16px 16px 0 16px;

				& > div {
					flex: 1;
				}
			}

			@media (max-width: 360px) {
				flex-wrap: wrap;
			}
		}
	}

	&__btn-register,
	&__btn-schedule {
		&,
		&-active {
			font-size: 14px;
			font-weight: 700;
			line-height: 17.07px;
			height: 36px !important;
			padding: 0 16px !important;
			text-transform: capitalize;
			border-radius: 8px;

			@media (max-width: 768px) {
				width: 100%;
			}
		}
	}

	&__btn-register {
		background-color: var(--button-secondary-background-color) !important;
		color: var(--button-secondary-color) !important;

		&-active {
			background-color: #9b9b9b !important;
			color: #fff !important;
		}

		&,
		&-active {
			@media (max-width: 360px) {
				margin-bottom: 8px;
			}
		}
	}

	&__btn-schedule {
		background-color: #fff !important;
		color: var(--button-secondary-background-color) !important;
		border: 2px solid var(--button-secondary-background-color);

		&-active {
			color: #9b9b9b !important;
			background-color: #fff !important;
			border: 2px solid #9b9b9b;
		}
	}

	&__thumbnail {
		&-wrapper {
			width: 100%;
			border-radius: 10px;
			position: relative;
			display: flex;
			flex-direction: column;
		}

		&-image {
			width: 100%;
			object-fit: cover;
			max-height: 325px;
			border-radius: 8px;
		}

		&-message-wrapper {
			overflow: hidden;
			padding: 12px 6px 6px 6px;
			background: #fff;
			border-radius: 10px 10px 0 0;
		}
	}

	&__divider {
		margin-top: 12px;
		border-color: #e2e2e2 !important;
	}
}
</style>
